// Promos de crédito del detalle de «Elige el teléfono», compactas: una fila por promo con qué hace
// (tasa, enganche, monto, bono) y su alcance en corto, más el chip del nivel al que aplica.
import { CreditCard } from 'lucide-react';
import { detailReachLabel, levelOnlyLabel, type CreditPromotionRow } from '../creditPromotions';
import { effectChips } from '../promotionDetail';
import type { PlanLevel } from '../types';
import { CompactPromotionGroup, CompactPromotionRow } from './CompactPromotionGroup';
import type { CreditGroupNote } from './CreditPromotionsGroup';
import { LevelChip } from './LevelChip';

const LABEL = 'Crédito';

const NOTE_TEXT: Record<Exclude<CreditGroupNote, 'notEligible'>, string> = {
    levelPending: 'según su nivel',
    atQuote: 'se aplica al cotizar con SureKeep',
};

/** «Tasa 150% · Bono de $500.00 · toda la tienda»; sin nivel resuelto agrega «Solo nivel Oro». */
export function compactCreditDetail(
    row: CreditPromotionRow,
    clientLevel: PlanLevel | null | undefined,
): string {
    const levelOnly = clientLevel === undefined ? levelOnlyLabel(row.targetLevels) : null;
    return [
        ...effectChips(row.effect).map((chip) => chip.short),
        detailReachLabel(row.reach),
        ...(levelOnly ? [levelOnly] : []),
    ].join(' · ');
}

export function CompactCreditPromotions({
    rows,
    clientLevel,
    note,
}: {
    rows: readonly CreditPromotionRow[];
    clientLevel?: PlanLevel | null | undefined;
    note?: CreditGroupNote | undefined;
}) {
    if (note === 'notEligible') {
        return (
            <CompactPromotionGroup
                label={LABEL}
                icon={CreditCard}
                tone="credit"
                items={
                    rows.length === 0
                        ? []
                        : [
                              {
                                  key: 'not-eligible',
                                  node: (
                                      <p className="py-0.5 text-muted-foreground">
                                          No disponible: el cliente no califica a crédito.
                                      </p>
                                  ),
                              },
                          ]
                }
            />
        );
    }
    return (
        <CompactPromotionGroup
            label={LABEL}
            icon={CreditCard}
            tone="credit"
            note={note === undefined ? undefined : NOTE_TEXT[note]}
            items={rows.map((row) => ({
                key: row.promotionId,
                node: (
                    <CompactPromotionRow
                        icon={CreditCard}
                        tone="credit"
                        name={row.name}
                        detail={compactCreditDetail(row, clientLevel)}
                        aside={
                            row.targetLevels.length > 0 && (
                                <span className="flex flex-none gap-0.5">
                                    {row.targetLevels.map((level) => (
                                        <LevelChip key={level} level={level} size="sm" />
                                    ))}
                                </span>
                            )
                        }
                    />
                ),
            }))}
        />
    );
}
